import { Injectable } from '@angular/core';
import { ApiClientService } from './api-client.service';
import { UrlService } from './url.service';
import { EmptyRowService } from './empty-row.service';
import { AuthenticationService } from './authentication.service';
import { PropertyService } from '../views/admin/property/services/property.service';
import { ParkingPassService } from './parking-pass.service';
import { SelectedBuilding } from '../types/data.types';
import { ISuiteUser } from '../types/api-response/building/suite-user';
import { AuthApiService } from './api/auth-api.service';
import { BuildingApiService } from './api/building-api.service';
import { IBuilding } from '../types/api-response/building';
import { ISuite } from '../types/api-response/building/suite';
import { map, mergeMap } from 'rxjs/operators';
import {CustomStyleService} from './custom-style.service';

declare const toastr: any;

@Injectable()
export class ResidentService {
    buildings: any = [];
    selectedBuilding: SelectedBuilding = {};
    dontShowWelcomeMessage: boolean = false;
    contactUsModalShow: boolean = false;
    showNoBuildingsModal: boolean = false;
    welcomeMessageInBuildings: any = [];
    user: any;
    serviceRequestSettings = {
        residentPermission: {
            serviceRequest: {
                create: false,
                read: false,
                attachment: false,
            },
            comment: {
                create: false,
                read: false,
                attachment: false,
            }
        },
        occupanciesAllowedToCreateServiceRequests: []
    };

    constructor(public api: ApiClientService,
        public urlService: UrlService,
        public emptyRowService: EmptyRowService,
        public authenticationService: AuthenticationService,
        private propertyService: PropertyService,
        public parkingPassService: ParkingPassService,
        private authApiService: AuthApiService,
        private buildingApiService: BuildingApiService,
        private customStyleService: CustomStyleService
    ) { }

    getBuildingsInfo() {
        if (this.authenticationService.userIsResident) {
            let entities: Array<ISuite & {building: {services: string[]}}> = [];

            return new Promise((resolve, reject) => {
                this.buildingApiService.getSuiteList({expand: 'building,building.timeZone,suiteUser,suiteUser.user'}).pipe(
                    mergeMap(suiteResult => {
                        const suites = suiteResult.entities;
                        let buildingIdList = suites.map(suite => (suite.building as IBuilding).id);
                        buildingIdList = buildingIdList.filter((v, i) => buildingIdList.indexOf(v) === i); // remove duplicates

                        return this.authApiService.getBuildingServices(buildingIdList).pipe(
                            map(servicesResult => suites.map((suite: ISuite & { building: { services: string[] } }) => {
                                const services = servicesResult.entities.filter(item => +item.buildingId === +(suite.building as IBuilding).id)[0];
                                suite.building.services = services ? services.serviceIdentifier : [];

                                return suite;
                            }))
                        );
                    })
                )
                .toPromise()
                .then(
                    (result) => entities = result,
                    (msg) => reject(msg)
                )
                .then(() => {
                    this.formattingBuildings(entities);
                    resolve();
                });
            });
        }
    }

    formattingBuildings(suiteList: Array<ISuite & {building: {services: string[]}}>) {
        if (this.authenticationService.userIsResident) {
            this.buildings = [];
            const suiteUsers = this.authenticationService.user.suiteUsers || [];
            const buildingPropertyManager = this.authenticationService.user.buildingPropertyManager || [];

            if(suiteUsers.length < 1)
                this.showNoBuildingsModal = true;

            suiteList.forEach((res) => {
                const building = res.building as IBuilding & {services: string[]};
                const usersListIdsInSuite = res.suiteUser.map(function (suiteUser: ISuiteUser) {
                   return +suiteUser.user.id;
                });

                const suite = suiteUsers.find(function (item) {
                    return item.suiteId == res.id && usersListIdsInSuite.includes(+item.residentBdId);
                });

                if (!suite) {
                    return;
                }

                const buildingPropertyManagerOne = buildingPropertyManager.find(function (item) {
                    return item.buildingId == building.id;
                });
                if (!buildingPropertyManagerOne) {
                    return;
                }

                const rolePermission = buildingPropertyManagerOne.rolePermissions || {};

                const suiteUser = res.suiteUser.find(u => u.user.id == suite.residentBdId);
                const data = {
                    buildingId: building.id,
                    buildingName: building.name,
                    buildingPostalCode: building.postalCode,
                    buildingTimezone: building.timeZone ? building.timeZone.value : undefined,
                    buildingAddress: building.address ? building.address : undefined,
                    welcomeMessage: null,
                    contactUs: building.contactUs,
                    suiteNumber: res.suiteNumber,
                    theme: buildingPropertyManagerOne.theme,
                    backgroundIMG: building.backgroundImage,
                    userName: this.authenticationService.userIsResident ? this.getResidentName(res, 'fullName') : this.authenticationService.user.fullName,
                    userEmail: this.authenticationService.user.email,
                    residentPhoto: this.getResidentName(res, 'photo'),
                    suite_id: suite.suiteId,
                    residentBdId: suite.residentBdId,
                    residentRole: suite.role,
                    cNSettings: building.cNSettings,
                    buildingServices: building.services,
                    userPhones: suiteUser && suiteUser.user ? suiteUser.user.phones : []
                };

                data['rolePermissions'] = [];
                data['rolePermissionsMenu'] = [];

                Object.keys(rolePermission).forEach(role => {
                    if (rolePermission[role].c) {
                        if(role === 'amenity' || role ===  'service_request' || role ===  'parking_pass') {
                            data['rolePermissions'].push(role);
                        }
                    }
                    if (rolePermission[role].r) {
                        data['rolePermissionsMenu'].push(role);
                    }
                });

                this.buildings.push(data);
            });

            localStorage.setItem('residentBuildings', JSON.stringify(this.buildings));

            if(localStorage.getItem('residentselectedBuilding')) {
                this.selectedBuilding = JSON.parse(localStorage.getItem('residentselectedBuilding'));
            } else {
                localStorage.setItem('residentselectedBuilding', JSON.stringify(this.buildings[0] || {}));
                this.selectedBuilding = JSON.parse(localStorage.getItem('residentselectedBuilding'));
                if(this.selectedBuilding.theme)
                    this.customStyleService.enableTheme(this.selectedBuilding.theme);
            }
            if(this.selectedBuilding && this.selectedBuilding.buildingId)
                this.getResidentServiceRequestPermissions();
            this.loadWelcomeMessage();
        }
    }

    updateWelcomeMessage() {
        const url = `/v1/hide_welcome_message/${this.selectedBuilding.buildingId}.json`;
        this.api.patch('domainBD', url, {}).subscribe(
            () => {
                toastr.success('Welcome message has been updated.');
                this.welcomeMessageInBuildings = [];
            },
            (error) => { this.propertyService.isWelcomeModalShow = false; }
        );
    }

    getWelcomeMessage() {
        if (this.authenticationService.userIsResident && this.selectedBuilding.buildingId) {
            const url = `/v1/building_welcome_messages`;
            this.api.get('domainBD', url).subscribe(
                res => {
                    const body = JSON.parse(res._body);
                    this.welcomeMessageInBuildings = body;
                    this.prepareWelcomeMessage();
                },
                error => this.api.errorHandler(error, 'Failed get welcome message'),
            );
        }
    }

    loadWelcomeMessage() {
        this.getWelcomeMessage();
        this.selectedBuilding.welcomeMessage = '';
    }

    prepareWelcomeMessage() {
        if (this.welcomeMessageInBuildings.length) {
            this.welcomeMessageInBuildings.map((item) => {
                let replacements = {};
                let mobilePhone;
                let homePhone;
                let businesPhone;
                if (item.id == this.selectedBuilding.buildingId) {
                    const residentId = JSON.parse(localStorage.getItem('residentselectedBuilding')).residentBdId;
                    const url = `/v1/residents/${residentId}.json?expand=phones,addresses,consent`;
                    this.api.get('domainBD', url).subscribe(
                        res => {
                            this.user = JSON.parse(res._body);

                            mobilePhone = this.user.phones.find(phone => phone.phone && phone.phoneType === 'phone_cell');

                            homePhone = this.user.phones.find(phone => phone.phone && phone.phoneType === 'phone_home');

                            businesPhone = this.user.phones.find(phone => phone.phone && phone.phoneType === 'phone_business');

                            replacements = {
                                '[[RESIDENT_NAME]]': this.user.fullName || ' ',
                                '[[RESIDENT_EMAIL]]': this.user.email || ' ',
                                '[[RESIDENT_MOBILE_PHONE]]': mobilePhone ? mobilePhone.phone : ' ',
                                '[[RESIDENT_HOME_PHONE]]':  homePhone ? homePhone.phone : ' ',
                                '[[RESIDENT_BUSINESS_PHONE]]': businesPhone ? businesPhone.phone : ' ',
                                '[[BUILDING_NAME]]': item.name || ' ',
                                '[[BUILDING_ADDRESS]]': item.address || ' '
                            };

                            const str: string = item.welcomeMessage || '';

                            this.selectedBuilding.welcomeMessage = str.replace(/\[(.*?)\]]/g, function (all) {
                                if (replacements[all] === ' ') replacements[all].split(' ');
                                return replacements[all] || all;
                            });

                            this.showWelcomeMessage();
                        },
                        error => this.api.errorHandler(error, 'Failed get user')
                    );
                }
            });
        }
    }

    showWelcomeMessage() {
        if (this.welcomeMessageInBuildings.length >= 1 && this.selectedBuilding.welcomeMessage && this.authenticationService.userIsResident) {
            this.propertyService.isWelcomeModalShow = true;
        }
    }

    getResidentLots(buildingId) {
        this.parkingPassService.residentParkinglots = [];
        this.parkingPassService.getLots(buildingId).subscribe(
            res => {
                this.parkingPassService.residentParkinglots = [];
                const entities = JSON.parse(res['_body']).entities || [];
                entities.forEach(element => {
                    for (let i = 0; i < element.lotRule.length; i++) {
                        if (element.lotRule[i].parkingRule.allowResidentsToBook) {
                            this.parkingPassService.residentParkinglots.push(element);
                            break;
                        }
                    }
                });
            }
        );
    }

    getResidentServiceRequestPermissions(): void {
        const url = `/v1/settings/buildings/${this.selectedBuilding.buildingId}`;
        this.api.get('domainServiceRequest', url).subscribe(
            res => {
                const body = JSON.parse(res._body);
                if (body && body.setting)
                    this.serviceRequestSettings = body.setting;
            }, error => toastr.error('Failed to get resident permissions')
        );
    }

    allowedForResident(service) {
        if(service == 'service_request') {
            let occupancyAllowed = true;
            if(localStorage.getItem('residentselectedBuilding')) {
                const selectedBuilding = JSON.parse(localStorage.getItem('residentselectedBuilding'));
                if(this.serviceRequestSettings.occupanciesAllowedToCreateServiceRequests.length > 0)
                    occupancyAllowed = this.serviceRequestSettings.occupanciesAllowedToCreateServiceRequests.findIndex(o => o.code == selectedBuilding.residentRole) != -1;
            }
            const allowedToRead = this.serviceRequestSettings.residentPermission.serviceRequest.read;
            return allowedToRead && occupancyAllowed;
        }
    }

    getResidentName(item, property) {
        const user = JSON.parse(localStorage.getItem('userData'));
        const match = item.suiteUser.find(u => user.suiteUsers.find(su => su.residentBdId == u.user.id));
        return match ? match.user[property] : '';
    }
}



// WEBPACK FOOTER //
// /app/src/app/services/resident.service.ts